import type { JSX, ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip.js';
import { cn } from '@/lib/cn.js';

export interface StatTileProps {
  label: string;
  value: ReactNode;
  /** Précision sous la valeur (« aujourd'hui », « 7 derniers jours »…). */
  hint?: string | undefined;
  /** Détail au survol — typiquement la répartition par origine. */
  tooltip?: ReactNode;
  /** Rend la tuile cliquable vers une liste filtrée. */
  to?: string | undefined;
  search?: Record<string, unknown> | undefined;
  /** Met la valeur en rouge quand elle demande une action (neutre à 0). */
  tone?: 'default' | 'alert';
  className?: string | undefined;
}

/**
 * Indicateur du tableau de bord (`02` §2) : libellé 12 px, chiffre clé,
 * précision. Sans `to`, c'est une cellule posée dans un panneau commun
 * (Activité) ; avec `to`, une tuile autonome cliquable qui mène à la liste
 * filtrée qui la détaille (À traiter).
 */
export function StatTile({
  label,
  value,
  hint,
  tooltip,
  to,
  search,
  tone = 'default',
  className,
}: StatTileProps): JSX.Element {
  const labelNode = (
    <span className="min-w-0 text-xs font-medium text-muted-foreground">{label}</span>
  );
  const body = (
    <>
      {to ? (
        <span className="flex items-center justify-between gap-2">
          {labelNode}
          <ChevronRight
            className="size-4 shrink-0 text-muted-foreground transition-colors group-hover:text-foreground"
            aria-hidden="true"
          />
        </span>
      ) : (
        labelNode
      )}
      <span
        className={cn(
          'text-2xl font-semibold leading-8 tracking-[-0.01em] tabular-nums',
          tone === 'alert' && value !== 0 ? 'text-status-red-fg' : 'text-foreground',
        )}
      >
        {value}
      </span>
      {hint ? <span className="text-xs tabular-nums text-muted-foreground">{hint}</span> : null}
    </>
  );

  const tile = to ? (
    <Link
      to={to}
      {...(search ? { search: search as never } : {})}
      className={cn(
        'group flex flex-col gap-0.5 rounded-lg border border-border bg-card px-4 py-3 transition-colors hover:bg-muted/60',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        className,
      )}
    >
      {body}
    </Link>
  ) : (
    <div className={cn('flex flex-col gap-0.5 px-5 py-4', className)}>{body}</div>
  );

  if (!tooltip) {
    return tile;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{tile}</TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}
